import type { CSSProperties } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  CircleCheck,
  CircleHelp,
  CirclePlus,
  ClipboardList,
  Clock,
  CornerDownRight,
  Download,
  FileSpreadsheet,
  FileText,
  Info,
  Link,
  MessageCircle,
  Pencil,
  Plus,
  Presentation,
  RotateCcw,
  Search,
  Table,
  Trash2,
  Upload,
  UploadCloud,
  X,
  type LucideIcon,
} from 'lucide-react';

/**
 * Phosphor → lucide mapping. The design project used the phosphor web font
 * (<i class="ph ph-check-circle">); this component keeps the original phosphor
 * glyph names as string keys so ported JSX stays diffable against the source.
 * Approximations (no exact lucide equivalent):
 *   microsoft-excel-logo → FileSpreadsheet, file-csv → FileSpreadsheet,
 *   file-pdf → FileText, presentation-chart → Presentation.
 */
const GLYPHS: Record<string, LucideIcon> = {
  'arrow-bend-down-right': CornerDownRight,
  'arrow-counter-clockwise': RotateCcw,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'caret-down': ChevronDown,
  'caret-right': ChevronRight,
  'caret-up': ChevronUp,
  'caret-up-down': ChevronsUpDown,
  check: Check,
  'check-circle': CircleCheck,
  'chat-circle-text': MessageCircle,
  'clipboard-text': ClipboardList,
  clock: Clock,
  'cloud-arrow-up': UploadCloud,
  'download-simple': Download,
  'file-csv': FileSpreadsheet,
  'file-pdf': FileText,
  info: Info,
  link: Link,
  'magnifying-glass': Search,
  'microsoft-excel-logo': FileSpreadsheet,
  'pencil-simple': Pencil,
  plus: Plus,
  'plus-circle': CirclePlus,
  presentation: Presentation,
  'presentation-chart': Presentation,
  question: CircleHelp,
  table: Table,
  trash: Trash2,
  'upload-simple': Upload,
  x: X,
};

interface PhProps {
  name: string;
  /** Font-size equivalent; phosphor icons sized via CSS font-size, default 1em. */
  size?: number | string;
  /**
   * phosphor "ph-fill" weight. lucide geometry is stroke-only (`check` is the open
   * path "M20 6 9 17l-5-5"), so an SVG `fill` turns it into a solid blob and
   * `circle-check` into a filled disc that hides the tick. Rendered as a heavier
   * stroke instead — the closest lucide has to phosphor's filled weight.
   */
  fill?: boolean;
  /** phosphor "ph-bold" weight → heavier stroke */
  bold?: boolean;
  style?: CSSProperties;
  className?: string;
}

export function Ph({ name, size = '1em', fill, bold, style, className }: PhProps) {
  const Glyph = GLYPHS[name];
  if (!Glyph) {
    if (import.meta.env.DEV) {
      console.warn(`[value-calculator] unmapped phosphor glyph: ${name}`);
    }
    return null;
  }
  return (
    <Glyph
      size={size}
      strokeWidth={bold || fill ? 2.5 : 2}
      fill="none"
      style={{ verticalAlign: '-0.125em', ...style }}
      className={className}
      aria-hidden
    />
  );
}
